import type { CreateServiceRequest } from "@/types/api";

export interface PostgresCdcFieldValues {
  name: string;
  targetService: string;
  /** The table to capture, as `schema.table`. */
  table: string;
  /** Seconds; blank keeps the engine's default. */
  heartbeatIntervalSecs?: string;
}

export type PostgresCdcErrors = Partial<
  Record<keyof PostgresCdcFieldValues, string>
>;

/** PostgreSQL's longest identifier, which a slot name must fit. */
const MAX_SLOT = 63;
const HASH_LENGTH = 8;

/**
 * The replication slot for capturing `table` through the service `name`:
 * both names, lowercased, with anything a slot name cannot hold turned into
 * `_`. A name too long for PostgreSQL keeps a prefix and a short hash of the
 * whole, so two long names stay distinct. The engine names the publication
 * after the slot.
 */
export function derivedSlot(name: string, table: string): string {
  const joined = `${name.trim()}_${table.trim()}`
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (joined.length <= MAX_SLOT) return joined;
  const keep = MAX_SLOT - HASH_LENGTH - 1;
  return `${joined.slice(0, keep)}_${fnv1a(joined)}`;
}

/** A stable 32-bit FNV-1a hash, as eight hex digits. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(HASH_LENGTH, "0");
}

/** Field-keyed problems with a change capture service, empty when none. */
export function validatePostgresCdc(
  fields: PostgresCdcFieldValues,
): PostgresCdcErrors {
  const errors: PostgresCdcErrors = {};
  if (!fields.name.trim()) errors.name = "A service name is required";
  if (!fields.targetService.trim()) {
    errors.targetService = "Choose the PostgreSQL service to capture from";
  }
  if (!fields.table.trim()) errors.table = "Choose the table to capture";
  else if (!derivedSlot(fields.name, fields.table)) {
    errors.name =
      "The service and table names need a letter or digit to name the slot";
  }
  const heartbeat = heartbeatError(fields.heartbeatIntervalSecs);
  if (heartbeat) errors.heartbeatIntervalSecs = heartbeat;
  return errors;
}

function heartbeatError(value: string | undefined): string | undefined {
  const text = value?.trim() ?? "";
  if (!text || isWholeSeconds(text)) return undefined;
  return "The heartbeat is a whole number of seconds, at least 1";
}

function isWholeSeconds(text: string): boolean {
  if (!/^\d+$/.test(text)) return false;
  const seconds = Number(text);
  return Number.isSafeInteger(seconds) && seconds >= 1;
}

/**
 * The service as the engine takes it. Throws when the fields are invalid.
 * No publication is sent: the engine creates and manages `<slot>_pub`.
 */
export function postgresCdcPayload(
  fields: PostgresCdcFieldValues,
): Partial<CreateServiceRequest> {
  const errors = Object.values(validatePostgresCdc(fields));
  if (errors.length > 0) throw new Error(errors[0]);
  const heartbeat = fields.heartbeatIntervalSecs?.trim();
  return {
    provider: "postgres_cdc",
    name: fields.name.trim(),
    target_service: fields.targetService.trim(),
    table: fields.table.trim(),
    slot: derivedSlot(fields.name, fields.table),
    ...(heartbeat ? { heartbeat_interval_secs: Number(heartbeat) } : {}),
  };
}
